/**
 * Prefixed, unguessable identifiers.
 *
 * Every public identifier carries 128 bits of CSPRNG entropy and a type prefix.
 * Two properties matter:
 *
 *  1. Enumeration is infeasible — there is no sequence to walk, and no ordering
 *     information leaks (unlike auto-increment or timestamp-prefixed IDs).
 *  2. Type confusion is caught at the boundary — a `fld_…` can never be passed
 *     where a `fil_…` is expected, because the Zod schema checks the prefix.
 *
 * Encoding is Crockford base32 (lowercase): no `i`, `l`, `o` or `u`, so an ID
 * read aloud or typed by hand is unambiguous.
 */

const ALPHABET = "0123456789abcdefghjkmnpqrstvwxyz";
const ENTROPY_BYTES = 16; // 128 bits → 26 encoded characters
const ENCODED_LENGTH = 26;

export const ID_PREFIXES = {
  user: "usr",
  session: "ses",
  device: "dev",
  group: "grp",
  folder: "fld",
  file: "fil",
  blob: "blb",
  variant: "var",
  invite: "inv",
  share: "shr",
  post: "pst",
  comment: "cmt",
  reaction: "rct",
  job: "job",
  export: "exp",
  reservation: "rsv",
  notification: "ntf",
  token: "tok",
  audit: "aud",
  /* The public half of an offline escrow key, registered by an operator. */
  recovery: "rec",
  grant: "gnt",
  escrow: "esc",
  recoveryuse: "rcu",
  /* Somebody waiting to be invited. Not an account, and never becomes one —
     the row stays as the record that the invitation was sent. */
  waitlist: "wlt",
} as const;

export type IdKind = keyof typeof ID_PREFIXES;

/**
 * The brand carrier.
 *
 * An exported interface rather than a `unique symbol`: declaration emit has to
 * be able to *name* the brand when it appears in an inferred type, and a
 * module-local symbol cannot be named from another package's `.d.ts`.
 * The property never exists at runtime.
 */
export interface IdBrand<K extends IdKind> {
  readonly __idKind: K;
}

/** A branded string: `Id<"file">` is not assignable to `Id<"folder">`. */
export type Id<K extends IdKind> = string & IdBrand<K>;

export type UserId = Id<"user">;
export type SessionId = Id<"session">;
export type DeviceId = Id<"device">;
export type GroupId = Id<"group">;
export type FolderId = Id<"folder">;
export type FileId = Id<"file">;
export type BlobId = Id<"blob">;
export type InviteId = Id<"invite">;
export type ShareId = Id<"share">;
export type PostId = Id<"post">;
export type CommentId = Id<"comment">;
export type ExportId = Id<"export">;
export type ReservationId = Id<"reservation">;

function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  // Available in Node >=19, all modern browsers, and React Native via polyfill.
  globalThis.crypto.getRandomValues(bytes);
  return bytes;
}

/** Crockford base32 encoding of arbitrary bytes. */
export function base32Encode(bytes: Uint8Array): string {
  let out = "";
  let buffer = 0;
  let bits = 0;

  for (const byte of bytes) {
    buffer = (buffer << 8) | byte;
    bits += 8;
    while (bits >= 5) {
      out += ALPHABET[(buffer >>> (bits - 5)) & 31];
      bits -= 5;
    }
  }
  if (bits > 0) {
    out += ALPHABET[(buffer << (5 - bits)) & 31];
  }
  return out;
}

/** Generate a new identifier of the given kind. */
export function newId<K extends IdKind>(kind: K): Id<K> {
  return `${ID_PREFIXES[kind]}_${base32Encode(randomBytes(ENTROPY_BYTES))}` as Id<K>;
}

const ID_PATTERN = new RegExp(`^[a-z]{3}_[${ALPHABET}]{${ENCODED_LENGTH}}$`);

/** Type guard: is `value` a well-formed identifier of the given kind? */
export function isId<K extends IdKind>(kind: K, value: unknown): value is Id<K> {
  return (
    typeof value === "string" &&
    ID_PATTERN.test(value) &&
    value.startsWith(`${ID_PREFIXES[kind]}_`)
  );
}

/**
 * Assert and narrow an untrusted string. Throws rather than returning a
 * falsy value so a missed check cannot silently proceed with a bad ID.
 */
export function assertId<K extends IdKind>(kind: K, value: unknown): Id<K> {
  if (!isId(kind, value)) {
    throw new TypeError(`Expected a ${kind} id (${ID_PREFIXES[kind]}_…)`);
  }
  return value;
}

/**
 * A high-entropy, URL-safe token for invite links and share links.
 * Not an ID: it is a bearer secret, so it is longer and carries no prefix.
 */
export function newToken(bytes = 20): string {
  return base32Encode(randomBytes(bytes));
}

/**
 * A human-typable access code, e.g. `FRIENDS-7X92-KD81`.
 *
 * Alphabet excludes visually ambiguous characters so a code read from a screen
 * and typed on a phone round-trips reliably. Three groups of four from a
 * 31-character alphabet is ~59 bits — far beyond brute-force reach at the
 * enforced rate limit, while staying short enough to read aloud.
 */
const CODE_ALPHABET = "23456789ABCDEFGHJKLMNPQRSTUVWXYZ";

export function newAccessCode(prefix?: string): string {
  const bytes = randomBytes(9);
  let out = "";
  for (let i = 0; i < 12; i++) {
    if (i > 0 && i % 4 === 0) out += "-";
    out += CODE_ALPHABET[bytes[i % bytes.length]! % CODE_ALPHABET.length];
  }
  const head = (prefix ?? "FRIENDS").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 12);
  return `${head || "FRIENDS"}-${out}`;
}

/** Codes are compared case-insensitively and ignoring separators. */
export function normalizeAccessCode(input: string): string {
  return input.toUpperCase().replace(/[^A-Z0-9]/g, "");
}
